import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useAudioVisualization, useSpeechRecognition, useTextToSpeech } from '../../hooks/audio';
import { useStreamingResponse } from '../../hooks/streaming';
import useNavigationGuard from '../../hooks/useNavigationGuard';
import axios from '../../utils/axiosConfig';
import { getBehavioralCorrections } from '../../utils/transcriptionUtils';
import ConversationView from './ConversationView';
import InterviewFeedback from './InterviewFeedback';
import InterviewHeader from './InterviewHeader';
import { LoadingState } from './LoadingErrorStates';
import RecordingControls from './RecordingControls';
import { FOCUS_AREAS, getRandomCompany } from './config/interviewConfig';
import ResumeUpload from './ResumeUpload';

/**
 * MockBehavioralInterview - Refactored version using extracted hooks and components
 * Original: ~3083 lines -> Refactored: ~450 lines
 */
const MockBehavioralInterview = ({ onQuestionsExported }) => {
  const { accessToken } = useAuth();

  // Company selection (random on mount)
  const [selectedCompany] = useState(getRandomCompany);

  // Interview state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isInterviewCompleted, setIsInterviewCompleted] = useState(false);
  const [speechEnabled, setSpeechEnabled] = useState(false);

  // Messages
  const [gptMessages, setGptMessages] = useState([
    {
      role: 'system',
      content: `Behavioral interview system for ${selectedCompany}`,
    },
    {
      role: 'assistant',
      content: `Hello! I'm here to conduct a behavioral interview for a software engineering internship position at ${selectedCompany}! Press 'Start Talking' and speak into the mic to begin your interview. How are you doing today?`,
    },
  ]);

  // Text input state
  const [textInput, setTextInput] = useState('');

  // Feedback state
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [feedbackError, setFeedbackError] = useState(null);
  const [interviewFeedback, setInterviewFeedback] = useState(null);

  // Recording state (managed locally, synced with hooks)
  const [isRecording, setIsRecording] = useState(false);

  // Speaking state (tracks when AI is speaking in audio mode)
  const [isSpeaking, setIsSpeaking] = useState(false);

  // Refs
  const conversationRef = useRef(null);
  const canvasRef = useRef(null);
  // Guards the (expensive, rate-limited) feedback request against duplicates.
  const feedbackRequestedRef = useRef(false);

  // Get behavioral word corrections. Memoized because useSpeechRecognition
  // keys an effect on this object's identity — recreating it every render
  // re-ran that effect on every render.
  const wordCorrections = useMemo(() => getBehavioralCorrections(), []);

  // Speech Recognition Hook
  const speechRecognition = useSpeechRecognition({
    wordCorrections,
    onError: (err) => setError(err),
  });

  // Audio Visualization Hook
  const audioVisualization = useAudioVisualization({
    canvasRef,
    isRecording,
  });

  // Text-to-Speech Hook
  const tts = useTextToSpeech({
    enabled: speechEnabled,
    voice: 'nova',
    speed: 1,
    onSpeakingChange: (speaking) => {
      // Speaking state is managed by the hook
    },
  });

  // Streaming Response Hook
  const streaming = useStreamingResponse({
    speechEnabled,
    generateAudio: tts.generateAudio,
    onError: (err) => setError(err),
  });

  // Initialize loading state
  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 1000);
    return () => clearTimeout(timer);
  }, []);

  // Release the mic on unmount.
  //
  // Deliberately does NOT acquire on mount: doing so fired the browser's
  // permission prompt (and lit the recording indicator) the instant the page
  // rendered, before the user had pressed anything. The stream is now acquired
  // lazily in handleStartRecording, matching the pre-refactor behavior.
  useEffect(() => {
    return () => audioVisualization.cleanup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-scroll conversation
  useEffect(() => {
    if (conversationRef.current) {
      conversationRef.current.scrollTop = conversationRef.current.scrollHeight;
    }
  }, [gptMessages, speechRecognition.transcript]);

  // Check for interview completion.
  //
  // Keyed off the streaming hook's `interviewComplete` flag rather than a
  // substring match on the rendered text: the sentinel is stripped before
  // display, and matching partial text used to fire this mid-stream — flipping
  // to the feedback screen before the interviewer finished its closing line,
  // then re-firing on every subsequent chunk (once per character-reveal tick in
  // audio mode), which spammed the feedback endpoint with requests carrying a
  // transcript truncated at that chunk.
  useEffect(() => {
    if (isInterviewCompleted) return;
    if (gptMessages.length === 0) return;

    const lastMessage = gptMessages[gptMessages.length - 1];
    if (
      lastMessage.role === 'assistant' &&
      lastMessage.interviewComplete &&
      !lastMessage.isStreaming
    ) {
      streaming.abort();
      setIsSpeaking(false);
      audioVisualization.cleanup();
      setIsInterviewCompleted(true);
      fetchInterviewFeedback();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gptMessages, isInterviewCompleted]);

  // True while the interviewer is mid-answer. Submitting during this window
  // aborts the in-flight fetch, freezing the interviewer's message mid-sentence
  // with a "(connection lost)" note that then rides along in the conversation
  // history on every later turn.
  // The conversation is seeded with a system message and the interviewer's
  // greeting, so anything beyond those two means the candidate has actually
  // started. Same threshold fetchInterviewFeedback uses to decide there is
  // enough conversation to be worth anything.
  useNavigationGuard({
    when: gptMessages.length > 2 && !isInterviewCompleted,
    message:
      'Leaving now ends this interview. Your transcript and progress will be lost. Are you sure?',
  });

  const isAssistantStreaming = gptMessages.some((msg) => msg.isStreaming);

  // Start recording handler
  const handleStartRecording = useCallback(async () => {
    // Don't let a new answer start on top of the interviewer's turn.
    if (isAssistantStreaming || isSpeaking) return;

    // Acquire the mic on demand (first press triggers the permission prompt).
    if (!audioVisualization.isInitialized) {
      const result = await audioVisualization.initialize();
      // Surface a denied/unavailable mic. This used to be swallowed by a bare
      // .catch(console.error) on the mount effect, leaving the user with a
      // permanently flat waveform and no explanation.
      if (result && result.success === false) {
        // result.error is an Error instance — never pass it to setError, it is
        // rendered directly and React throws on a non-primitive child.
        setError(
          'Could not access your microphone. Check your browser permissions, or type your answer instead.'
        );
        return;
      }
    }

    // Reset transcript first
    speechRecognition.reset();

    // Try to start speech recognition
    const started = speechRecognition.start();
    if (started) {
      setIsRecording(true);
    } else {
      // Speech recognition failed to start
      setError('Failed to start speech recognition. Please try again.');
    }
  }, [speechRecognition, audioVisualization, isAssistantStreaming, isSpeaking]);

  // Stop recording handler
  const handleStopRecording = useCallback(() => {
    setIsRecording(false);
    speechRecognition.stop();

    const transcript = speechRecognition.transcript.trim();
    if (transcript) {
      // Add user message
      setGptMessages((prev) => [...prev, { role: 'user', content: transcript }]);
      chatWithLLM(transcript);
    }

    speechRecognition.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [speechRecognition]);

  // Chat with LLM
  const chatWithLLM = useCallback(
    async (userMessage) => {
      const TRIAL_BLOCKED_MESSAGE =
        "Basic trial doesn't allow AI mock interviews. Ask your university to get InternHub to unlock this feature.";

      try {
        // Check trial access.
        //
        // The has_access test lives OUTSIDE the try: it used to sit inside,
        // where its own `throw` was immediately caught by the adjacent catch
        // (a thrown Error has no `.response`, so the 403 test failed) and
        // silently swallowed — letting a blocked trial user run the interview
        // anyway. Only a hard 403 was ever enforced.
        let hasAccess = true;
        try {
          const trialResponse = await axios.get(
            '/api/v1/trial/check-access/behavioral_mock_interview'
          );
          hasAccess = trialResponse.data?.has_access !== false;
        } catch (trialError) {
          if (trialError.response?.status === 403) {
            hasAccess = false;
          }
          // Any other failure (network, 5xx) is not a licensing signal —
          // fail open rather than blocking a paying user on a blip.
        }

        if (!hasAccess) {
          throw new Error(TRIAL_BLOCKED_MESSAGE);
        }

        // Prepare messages for API
        const formattedMessages = gptMessages
          .filter((msg) => msg.role !== 'system')
          .map((msg) => ({ role: msg.role, content: msg.content }));

        formattedMessages.push({ role: 'user', content: userMessage });

        // Get abort signal
        const signal = streaming.getAbortSignal();

        // Make streaming request
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/v1/anthropic/behavioral-interview-stream`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify({
              messages: formattedMessages,
              company_name: selectedCompany,
              focus_areas: FOCUS_AREAS,
            }),
            signal,
          }
        );

        if (!response.ok) {
          throw new Error(`API call failed: ${response.status}`);
        }

        const assistantMessageId = Date.now();

        // Add empty assistant message
        setGptMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            content: '',
            id: assistantMessageId,
            isStreaming: true,
          },
        ]);

        // Process streaming response
        await streaming.streamResponse(
          response,
          (update) => {
            setGptMessages((prev) =>
              prev.map((msg) => (msg.id === assistantMessageId ? { ...msg, ...update } : msg))
            );
          },
          (speaking) => {
            // Track speaking state from audio mode for UI updates
            setIsSpeaking(speaking);
          }
        );
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Error with streaming API:', err);
          setError(err.message || 'Sorry, there was an error processing your request.');
        }
      }
    },
    [accessToken, gptMessages, selectedCompany, streaming]
  );

  // Text input submit handler
  const handleTextInputSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!textInput.trim()) return;
      // Don't let a submission interrupt the interviewer's turn.
      if (isAssistantStreaming || isSpeaking) return;

      const message = textInput.trim();
      setTextInput('');

      // Add user message
      setGptMessages((prev) => [...prev, { role: 'user', content: message }]);
      await chatWithLLM(message);
    },
    [textInput, chatWithLLM, isAssistantStreaming, isSpeaking]
  );

  // Fetch interview feedback
  const fetchInterviewFeedback = useCallback(async () => {
    // Belt-and-braces against duplicate submissions: the completion effect is
    // guarded, but "End Interview" can also race with auto-completion, and this
    // request is expensive and rate-limited.
    if (feedbackRequestedRef.current) return;

    if (gptMessages.length <= 2) {
      setFeedbackError('Not enough conversation to generate feedback');
      return;
    }

    feedbackRequestedRef.current = true;
    setFeedbackLoading(true);
    setFeedbackError(null);

    try {
      const transcript = gptMessages.filter((msg) => msg.role !== 'system');
      const response = await axios.post('/api/v1/anthropic/analyze-interview-feedback', {
        focus_areas: FOCUS_AREAS,
        interview_transcript: transcript,
      });

      // A truncated analysis is a 200 carrying an empty report. Presenting it
      // as the assessment would be worse than an error — surface it as one and
      // release the duplicate-request guard so the retry can actually run.
      if (response.data?.truncated) {
        feedbackRequestedRef.current = false;
        setFeedbackError(
          response.data.overall_feedback ||
            'The analysis ran out of room before it finished. Please try again.'
        );
        return;
      }

      setInterviewFeedback(response.data);
    } catch (err) {
      console.error('Error getting interview feedback:', err);
      // Allow another attempt; the failure may well be transient.
      feedbackRequestedRef.current = false;
      setFeedbackError('Sorry, there was an error generating feedback. Please try again.');
    } finally {
      setFeedbackLoading(false);
    }
  }, [gptMessages]);

  // Export to Q&A Bank
  const exportToQABank = useCallback(async () => {
    try {
      const qaResponses = [];
      let currentQuestion = null;

      const cleanText = (text) => (text ? text.replace(/\s+/g, ' ').trim() : '');

      const extractQuestionFromMessage = (message) => {
        const paragraphs = message.split(/\n\s*\n/).filter((p) => p.trim());
        if (paragraphs.length > 0) {
          return paragraphs[paragraphs.length - 1].trim();
        }
        const fallback = message.trim();
        if (fallback.length > 200) {
          const sentences = fallback.split(/[.!?]+/).filter((s) => s.trim());
          if (sentences.length > 0) {
            return sentences[sentences.length - 1].trim() + '?';
          }
        }
        return fallback;
      };

      gptMessages.forEach((message) => {
        if (
          message.role === 'assistant' &&
          message.content &&
          !message.content.includes('Hello!') &&
          !message.content.includes('END HERE')
        ) {
          const extractedQuestion = extractQuestionFromMessage(message.content);
          if (extractedQuestion) {
            currentQuestion = cleanText(extractedQuestion);
          }
        } else if (message.role === 'user' && currentQuestion && message.content?.trim()) {
          qaResponses.push({
            question: currentQuestion,
            answer: cleanText(message.content),
          });
          currentQuestion = null;
        }
      });

      if (qaResponses.length === 0) {
        alert('No Q&A pairs found to export.');
        return;
      }

      const userID = localStorage.getItem('userID');
      if (!userID) {
        throw new Error('User ID not found. Please log in again.');
      }

      const response = await axios.post('/api/v1/resume/export-interview-responses', {
        interview_responses: qaResponses,
        user_id: parseInt(userID),
      });

      alert(
        `Successfully exported ${response.data.exported_count} interview responses to your Q&A bank!`
      );

      if (onQuestionsExported) {
        onQuestionsExported();
      }
    } catch (err) {
      console.error('Error exporting responses:', err);
      alert(`Failed to export interview responses: ${err.message}`);
    }
  }, [gptMessages, onQuestionsExported]);

  // Restart interview
  const restartInterview = useCallback(() => {
    // Abort streaming. This also silences any queued interview audio — without
    // it the interviewer kept talking over the fresh session.
    streaming.abort();

    // Stop recording
    if (isRecording) {
      speechRecognition.stop();
      setIsRecording(false);
    }

    // Stop TTS
    tts.stop();

    // Drop any transcript left over from the previous session, so a half-spoken
    // answer can't leak into the new interview's first turn.
    speechRecognition.reset();

    // Cleanup visualization
    audioVisualization.cleanup();

    // Reset state
    setIsInterviewCompleted(false);
    setInterviewFeedback(null);
    setFeedbackError(null);
    setTextInput('');
    setError(null);
    setIsSpeaking(false);
    feedbackRequestedRef.current = false;

    // Reset messages
    setGptMessages([
      {
        role: 'system',
        content: `Behavioral interview system for ${selectedCompany}`,
      },
      {
        role: 'assistant',
        content: `Hello! I'm here to conduct a behavioral interview for a software engineering internship position at ${selectedCompany}! Press 'Start Talking' and speak into the mic to begin your interview. How are you doing today?`,
      },
    ]);

    // The visualizer re-acquires the mic lazily on the next "Start Talking",
    // so there is deliberately no initialize() here.
  }, [isRecording, speechRecognition, tts, audioVisualization, streaming, selectedCompany]);

  // End interview manually
  const endInterview = useCallback(() => {
    // Flush whatever the user had already said before tearing down, so a
    // half-spoken final answer is graded rather than discarded.
    const pending = speechRecognition.transcript.trim();
    if (isRecording) {
      speechRecognition.stop();
      setIsRecording(false);
    }
    if (pending) {
      setGptMessages((prev) => [...prev, { role: 'user', content: pending }]);
    }
    speechRecognition.reset();

    streaming.abort();
    tts.stop();
    audioVisualization.cleanup();
    setIsSpeaking(false);
    setIsInterviewCompleted(true);
    fetchInterviewFeedback();
  }, [isRecording, speechRecognition, tts, streaming, audioVisualization, fetchInterviewFeedback]);

  // Resume upload handler
  const handleResumeUploaded = useCallback((resume) => {
    console.log('Resume uploaded:', resume);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      streaming.abort();
      speechRecognition.stop();
      tts.cleanup();
      audioVisualization.cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Render loading state
  if (loading) {
    return <LoadingState />;
  }

  // NOTE: there is deliberately no full-screen error branch here.
  //
  // Every speech-recognition hiccup (a transient `audio-capture`, a denied
  // prompt) and every stream error used to replace the entire component with
  // ErrorState, whose only action was window.location.reload() — discarding the
  // whole transcript and making the STAR summary unreachable. Recoverable
  // errors now render as a dismissible banner above the conversation instead
  // (see below), keeping the interview intact.

  // Render completed interview with feedback
  if (isInterviewCompleted) {
    return (
      <InterviewFeedback
        selectedCompany={selectedCompany}
        focusAreas={FOCUS_AREAS}
        feedback={interviewFeedback}
        feedbackLoading={feedbackLoading}
        feedbackError={feedbackError}
        gptMessages={gptMessages}
        onExportToQABank={exportToQABank}
        onRestartInterview={restartInterview}
        onRetryFeedback={fetchInterviewFeedback}
      />
    );
  }

  // Render main interview UI
  return (
    <div className="flex flex-col grow h-full">
      <div className="flex flex-col bg-white border shadow-lg rounded-xl w-full h-full overflow-hidden">
        <InterviewHeader
          speechEnabled={speechEnabled}
          onSpeechToggle={() => setSpeechEnabled(!speechEnabled)}
          onEndInterview={endInterview}
          isToggleDisabled={
            // Disable toggle while assistant is streaming or speaking
            isAssistantStreaming || isSpeaking
          }
        />

        {error && (
          <div
            role="alert"
            className="flex-none flex items-start gap-3 px-4 py-3 bg-red-50 border-b border-red-200"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 text-red-500 shrink-0 mt-0.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <p className="text-sm text-red-800 grow">{error}</p>
            <button
              type="button"
              onClick={() => setError(null)}
              className="text-sm font-medium text-red-700 hover:text-red-900 shrink-0"
              aria-label="Dismiss error"
            >
              Dismiss
            </button>
          </div>
        )}

        <div className="flex-none px-4 py-2 border-b border-gray-200 bg-gray-50">
          <ResumeUpload onResumeUploaded={handleResumeUploaded} />
        </div>

        <ConversationView
          messages={gptMessages}
          transcript={speechRecognition.transcript}
          isRecording={isRecording}
          speechEnabled={speechEnabled}
          conversationRef={conversationRef}
        />

        <RecordingControls
          isRecording={isRecording}
          // Treat "interviewer is still answering" like "interviewer is
          // speaking" for input-gating purposes: submitting here aborts its
          // turn mid-sentence and poisons the conversation history.
          isSpeaking={isSpeaking || isAssistantStreaming}
          speechEnabled={speechEnabled}
          speechRecognitionStatus={speechRecognition.status}
          canvasRef={canvasRef}
          textInput={textInput}
          onTextInputChange={(e) => setTextInput(e.target.value)}
          onTextInputSubmit={handleTextInputSubmit}
          onStartRecording={handleStartRecording}
          onStopRecording={handleStopRecording}
          isSpeechRecognitionSupported={speechRecognition.isSupported}
        />
      </div>
    </div>
  );
};

export default MockBehavioralInterview;
